// FP3-035A — Student Viewer (public-by-token).
//
// Polls /api/articulation-screener/viewer/{token} every ~1.5 seconds
// for the provider's current item index and renders the picture
// prompt + target word + sound + position chip. Shows NO PHI: no
// student name, no scoring, no notes, no provider info. Token is a
// 24-byte url-safe secret minted server-side; revoking the session
// invalidates the URL.
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Eye, CalendarSlash, ArrowsClockwise } from "@phosphor-icons/react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATIC_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 1500;

const POSITION_LABEL = {
  initial: "Initial",
  medial: "Medial",
  final: "Final",
  blend: "Blend",
};

export default function StudentViewer() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    let timer = null;
    const poll = async () => {
      try {
        const r = await axios.get(
          `${API_URL}/articulation-screener/viewer/${token}`,
        );
        if (!aliveRef.current) return;
        setState(r.data);
        setError(null);
      } catch (err) {
        if (!aliveRef.current) return;
        const status = err?.response?.status;
        if (status === 404) {
          setError("Session ended or not found");
          setState(null);
        } else {
          setError("Connection lost — retrying");
        }
      }
      if (aliveRef.current) {
        timer = setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => {
      aliveRef.current = false;
      if (timer) clearTimeout(timer);
    };
  }, [token, retrying]);

  const renderEnded = () => (
    <div
      data-testid="student-viewer-ended"
      className="min-h-screen w-full bg-[#FBFAF6] flex flex-col items-center justify-center px-6 text-center"
    >
      <div className="w-20 h-20 rounded-full bg-[#F1EFE8] flex items-center justify-center mb-6">
        <CalendarSlash size={36} weight="duotone" className="text-[#9C6B2E]" />
      </div>
      <h1 className="text-2xl font-semibold text-[#1F2937] mb-2">
        Session ended
      </h1>
      <p className="text-sm text-[#6B7280] max-w-md">
        This screener viewer is no longer active. Ask the provider to
        start a new session if you need to continue.
      </p>
      <button
        type="button"
        onClick={() => setRetrying((n) => !n)}
        data-testid="student-viewer-retry"
        className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[#1E3F33] hover:underline"
      >
        <ArrowsClockwise size={14} /> Retry
      </button>
    </div>
  );

  if (error && !state) return renderEnded();
  if (!state) {
    return (
      <div
        data-testid="student-viewer-loading"
        className="min-h-screen w-full bg-[#FBFAF6] flex items-center justify-center"
      >
        <div className="flex flex-col items-center gap-3 text-[#6B7280]">
          <Eye size={32} weight="duotone" />
          <span className="text-xs uppercase tracking-wider">Waiting for provider…</span>
        </div>
      </div>
    );
  }

  if (state.status === "ended") return renderEnded();

  const cur = state.current_item;
  const positionLabel = cur ? POSITION_LABEL[cur.word_position] || cur.word_position : null;

  return (
    <div
      data-testid="student-viewer-root"
      className="min-h-screen w-full bg-[#FBFAF6] text-[#1F2937] flex flex-col"
    >
      {/* Slim top bar with no PHI — just session progress */}
      <header className="px-6 sm:px-10 pt-5 pb-3 flex items-center justify-between text-[11px] uppercase tracking-wider text-[#6B7280] border-b border-[#ECEAE4] bg-white/60 backdrop-blur">
        <span className="inline-flex items-center gap-1.5 font-semibold">
          <Eye size={12} weight="fill" className="text-[#1E3F33]" />
          Student viewer
        </span>
        <span data-testid="student-viewer-progress">
          {state.total_items > 0
            ? `Item ${state.current_item_index + 1} of ${state.total_items}`
            : "—"}
        </span>
      </header>

      {/* Main picture-prompt stage */}
      <main className="flex-1 flex items-center justify-center px-6 sm:px-10 py-8">
        {cur && cur.image_url ? (
          <div
            data-testid="student-viewer-card"
            className="bg-white rounded-3xl shadow-[0_25px_60px_-25px_rgba(31,41,55,0.18)] border border-[#ECEAE4] px-6 sm:px-10 py-8 sm:py-10 w-full max-w-3xl"
          >
            {/* Stable bounded image frame — fixed aspect, generous
                padding, object-contain. Never clips regardless of
                viewport size or source page dimensions. */}
            <div
              data-testid="student-viewer-image-frame"
              className="w-full mx-auto bg-[#FBFAF6] rounded-2xl border border-[#ECEAE4] flex items-center justify-center p-4 sm:p-6 mb-6 sm:mb-8"
              style={{ aspectRatio: "4 / 5", maxHeight: "62vh" }}
            >
              <img
                data-testid="student-viewer-image"
                src={`${STATIC_URL}${cur.image_url}`}
                alt={cur.target_word || ""}
                draggable={false}
                className="max-w-full max-h-full object-contain select-none pointer-events-none"
                style={{ display: "block" }}
              />
            </div>
            <div className="text-center">
              <p
                data-testid="student-viewer-target-word"
                className="text-5xl sm:text-7xl font-extrabold tracking-tight text-[#1F2937] mb-3"
                style={{ fontFamily: '"Manrope", system-ui, sans-serif' }}
              >
                {cur.target_word}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2 mt-3">
                <span
                  data-testid="student-viewer-sound-chip"
                  className="text-xs sm:text-sm uppercase tracking-wide font-semibold px-3 py-1 rounded-full bg-[#E8F0EB] text-[#1E3F33]"
                >
                  Sound {cur.target_sound}
                </span>
                {positionLabel && (
                  <span
                    data-testid="student-viewer-position-chip"
                    className="text-xs sm:text-sm uppercase tracking-wide font-semibold px-3 py-1 rounded-full bg-[#FDF4E5] text-[#9C6B2E]"
                  >
                    {positionLabel}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#6B7280]">No active item.</p>
        )}
      </main>

      <footer className="px-6 py-3 text-center text-[10px] uppercase tracking-wider text-[#9B9890] border-t border-[#ECEAE4] bg-white/60">
        {state.template_name}
      </footer>
    </div>
  );
}
